import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import type { AccordionProps } from '@mui/material/Accordion';
import MuiAccordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import { styled } from '@mui/material/styles';
import * as React from 'react';
import Skeleton from 'react-loading-skeleton';

import { trackEvent } from '@/analytics/trackEvent';
import { Tooltip } from '@/components/Atoms/Tooltip/Tooltip';
import type { AskNeedlReportType } from '@/features/ChatBotV2/context';
import { useReportSourcesStatus } from '@/features/ChatBotV2/hooks/useReportSourcesStatus';
import { getMixpanelLabelForReportType } from '@/features/NeedlReport/common';
import { useGetInitialQuestions } from '@/features/NeedlReport/hooks/useGetInitialQuestions';
import { useSelectReportFileIds } from '@/features/NeedlReport/hooks/useReport';
import SuggestedQuestionIcon from '@/icons/suggested_questions.svg?react';

const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters elevation={0} {...props} />
))(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.divider}`,
  '&:last-child': {
    borderBottom: 0,
  },
  backgroundColor: 'transparent',
  '&::before': {
    display: 'none',
  },
}));

export const NeedlReportQuestions = ({
  report,
  currentFeed,
  onQuestionSelect,
}: NeedlReportQuestionsType) => {
  const { completedCount } = useReportSourcesStatus(report);
  const { selectedIds } = useSelectReportFileIds();
  const { data: questions, isFetching } = useGetInitialQuestions({
    feedId: currentFeed,
    enabled: completedCount > 0,
  });

  const handleQuestionSelect = React.useCallback(
    (question: string) => {
      trackEvent(
        `${getMixpanelLabelForReportType(
          report.info?.id
        )}_chat_suggested_prompt_clicked`,
        {
          question,
        }
      );
      onQuestionSelect(question);
    },
    [onQuestionSelect, report.info?.id]
  );

  React.useEffect(() => {
    if (!isFetching && questions) {
      trackEvent(
        `${getMixpanelLabelForReportType(
          report.info?.id
        )}_chat_suggested_prompt_displayed`
      );
    }
  }, [questions, isFetching, report.info?.id]);

  if (completedCount === 0) {
    return null;
  }

  return (
    <div className='flex flex-col gap-3 w-full pb-12'>
      <div className='flex items-center gap-2 text-gray-700'>
        <div className='w-6 h-6'>
          <SuggestedQuestionIcon />
        </div>
        <div className='font-medium'>Suggested Questions</div>
      </div>
      {isFetching ? (
        <div className='flex flex-col gap-1'>
          {Array(3)
            .fill(0)
            .map((_, index) => (
              <Skeleton key={index} height={36} />
            ))}
        </div>
      ) : (
        questions && (
          <div className='flex flex-col'>
            {Object.entries(questions).map(([category, questionsList]) => (
              <QuestionGroup
                key={category}
                category={category}
                questions={questionsList}
                onQuestionSelect={handleQuestionSelect}
                disabled={selectedIds?.length === 0}
              />
            ))}
          </div>
        )
      )}
    </div>
  );
};

const QuestionGroup = ({
  category,
  questions,
  onQuestionSelect,
  disabled = false,
}: QuestionGroupType) => {
  const detailsRef = React.useRef<HTMLDivElement | null>(null);

  const handleChange = (event: React.SyntheticEvent, isExpanded: boolean) => {
    if (isExpanded && detailsRef.current) {
      setTimeout(() => {
        detailsRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
      }, 200);
    }
  };

  return (
    <Accordion key={category} onChange={handleChange}>
      <AccordionSummary
        sx={{ paddingLeft: 1, paddingRight: 1 }}
        className='hover:bg-gray-200'
        expandIcon={<KeyboardArrowDownIcon />}
        aria-controls={`${category}-content`}
        id={`${category}-header`}
      >
        <div className='font-medium'>{category}</div>
      </AccordionSummary>
      <AccordionDetails ref={detailsRef}>
        <div className='flex flex-col gap-2 pb-4'>
          {questions.map((question) => (
            <Tooltip
              label={disabled ? 'Select a source first.' : ''}
              key={`${category}-${question}`}
            >
              <button
                disabled={disabled}
                className='hover:text-emerald-900 transition-colors text-left py-1 pl-2 disabled:cursor-not-allowed'
                onClick={() => onQuestionSelect(question)}
              >
                {question}
              </button>
            </Tooltip>
          ))}
        </div>
      </AccordionDetails>
    </Accordion>
  );
};

type QuestionGroupType = {
  category: string;
  questions: string[];
  onQuestionSelect: (values: string) => void;
  disabled?: boolean;
};

type NeedlReportQuestionsType = {
  report: AskNeedlReportType;
  currentFeed: string;
  onQuestionSelect: (values: string) => void;
};
